/**
 * Phase 8 T-18 §26.A — Sync status badge.
 *
 * Three states per §26.A:
 *   (a) up-to-date  → emerald dot · "Everything up to date" · zinc-600 text
 *   (b) syncing     → amber dot (800 ms pulse) · "Syncing…"
 *   (c) failed      → red dot · red text · "Retry" underlined action
 *
 * Renders at the top-left of the main content area, just below the
 * breadcrumb line. Not visible in the Topbar.
 */

import { useSyncStatus } from '@shared/hooks/useSyncStatus';

interface SyncStatusBadgeProps {
  className?: string;
}

export function SyncStatusBadge({ className = '' }: SyncStatusBadgeProps) {
  const { status, retry } = useSyncStatus();

  if (status === 'up-to-date') {
    return (
      <div
        className={`flex items-center gap-1.5 ${className}`}
        aria-live="polite"
        aria-label="Data sync status: everything up to date"
      >
        <span
          className="h-1.5 w-1.5 flex-shrink-0 rounded-full"
          style={{ background: '#10B981' }}
          aria-hidden
        />
        <span
          className="text-[11px]"
          style={{ color: 'var(--vscode-text-tertiary)' }}
        >
          Everything up to date
        </span>
      </div>
    );
  }

  if (status === 'syncing') {
    return (
      <div
        className={`flex items-center gap-1.5 ${className}`}
        aria-live="polite"
        aria-label="Data sync status: syncing"
      >
        <span
          className="h-1.5 w-1.5 flex-shrink-0 animate-pulse rounded-full"
          style={{
            background: '#F59E0B',
            animationDuration: '800ms',
          }}
          aria-hidden
        />
        <span
          className="text-[11px]"
          style={{ color: 'var(--vscode-text-tertiary)' }}
        >
          Syncing…
        </span>
      </div>
    );
  }

  // status === 'failed'
  return (
    <div
      className={`flex items-center gap-1.5 ${className}`}
      aria-live="assertive"
      aria-label="Data sync status: sync failed"
    >
      <span
        className="h-1.5 w-1.5 flex-shrink-0 rounded-full"
        style={{ background: '#EF4444' }}
        aria-hidden
      />
      <span className="text-[11px]" style={{ color: '#EF4444' }}>
        Sync failed —{' '}
        <button
          type="button"
          onClick={retry}
          className="underline underline-offset-2 transition-opacity hover:opacity-80"
          style={{ color: '#EF4444' }}
        >
          Retry
        </button>
      </span>
    </div>
  );
}
